import React, { useEffect, useRef, useState, type CSSProperties } from 'react';
import { colors } from '../../elements/colors';
import { duration, skeletonMotion } from '../../elements/animations';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import type { SkeletonBlockProps } from './SkeletonBlock';

type WebProps = Partial<SkeletonBlockProps> & { 'data-testid'?: string };
const motionCss = `[data-skeleton-highlight] { visibility: hidden; }
@keyframes galapagos-skeleton-sweep { from { transform: translateX(var(--skeleton-from)) rotate(${skeletonMotion.tiltDegrees}deg); } to { transform: translateX(var(--skeleton-to)) rotate(${skeletonMotion.tiltDegrees}deg); } }
@media (prefers-reduced-motion: no-preference) { [data-galapagos-skeleton-active="true"] > [data-skeleton-highlight] { visibility: visible; animation: galapagos-skeleton-sweep ${duration.shimmer}ms linear infinite; } }`;

export function SkeletonBlock({
  width = 'fill',
  height = spacing.m,
  radius: corner = 'm',
  active = true,
  testID,
  'data-testid': dataTestId,
}: WebProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = () => {
      const value = node.getBoundingClientRect().width;
      setMeasuredWidth(Number.isFinite(value) && value > 0 ? value : 0);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [width]);
  const bandWidth = Math.min(sizing.skeleton.highlightWidth, measuredWidth);
  const tilt = (skeletonMotion.tiltDegrees * Math.PI) / 180;
  const bandHeight = height / Math.cos(tilt) + bandWidth * Math.tan(tilt);
  const inset = bandWidth + height * Math.tan(tilt);
  const style: CSSProperties & { '--skeleton-from': string; '--skeleton-to': string } = {
    width: width === 'fill' ? '100%' : width,
    height,
    borderRadius: radius[corner],
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: colors.background.secondary,
    '--skeleton-from': `${-inset}px`,
    '--skeleton-to': `${measuredWidth + inset}px`,
  };
  return (
    <div
      ref={ref}
      aria-hidden="true"
      data-testid={dataTestId ?? testID}
      style={style}
      data-galapagos-skeleton-active={active && measuredWidth > 0 ? 'true' : 'false'}
    >
      <style>{motionCss}</style>
      <div
        data-skeleton-highlight="true"
        style={{
          position: 'absolute',
          left: 0,
          top: (height - bandHeight) / 2,
          width: bandWidth,
          height: bandHeight,
          pointerEvents: 'none',
          background: `linear-gradient(to right, ${colors.nav.gradientStart}, ${colors.nav.gradientEnd}, ${colors.nav.gradientStart})`,
        }}
      />
    </div>
  );
}
